import { useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type { ClientToolRow } from '@/types/coach';
import type { ToolKey } from '@/lib/tools';

interface Props {
  clientId: string;
  hasEmail: boolean;
}

function formatRelative(iso: string | null): string {
  if (!iso) return 'noch nicht aktiv';
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return 'gerade eben';
  if (minutes < 60) return `vor ${minutes} Min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} Std`;
  const days = Math.round(hours / 24);
  return `vor ${days} Tag${days === 1 ? '' : 'en'}`;
}

export function ClientAppsTab({ clientId, hasEmail }: Props) {
  const [tools, setTools] = useState<ClientToolRow[] | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ClientToolRow | null>(null);
  const [busy, setBusy] = useState<ToolKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.coach
      .listClientTools(clientId)
      .then((rows) => {
        if (!cancelled) setTools(rows);
      })
      .catch((err) => {
        toast.error(err instanceof Error ? err.message : 'Apps konnten nicht geladen werden.');
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  async function handleGrant(row: ClientToolRow) {
    if (!hasEmail || row.status !== 'active') return;
    setBusy(row.toolKey);
    try {
      const res = await api.coach.grantClientTool(clientId, row.toolKey);
      setTools((prev) =>
        prev?.map((t) => (t.toolKey === row.toolKey ? { ...t, granted: true } : t)) ?? null,
      );
      toast.success(
        res.mailed
          ? 'Freigeschaltet. Klient hat eine Einladung per E-Mail bekommen.'
          : 'Freigeschaltet.',
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Freischaltung fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  }

  async function confirmRevoke() {
    if (!pendingRevoke) return;
    const row = pendingRevoke;
    setPendingRevoke(null);
    setBusy(row.toolKey);
    try {
      await api.coach.revokeClientTool(clientId, row.toolKey);
      setTools((prev) =>
        prev?.map((t) => (t.toolKey === row.toolKey ? { ...t, granted: false } : t)) ?? null,
      );
      toast.success('Zugang entzogen.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Aktion fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  }

  if (!tools) {
    return <p className="text-sm text-muted-foreground">Lade Apps …</p>;
  }

  return (
    <div className="space-y-4">
      {!hasEmail && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Bitte erst eine E-Mail-Adresse beim Klienten erfassen, dann kannst du Apps freischalten.
        </div>
      )}

      <div className="rounded-2xl border border-border divide-y divide-border">
        {tools.map((row) => {
          const isActive = row.status === 'active';
          const disabled = !isActive || !hasEmail || busy === row.toolKey;
          const subtext = !isActive
            ? 'Kommt bald'
            : row.granted
            ? row.lastUsedAt
              ? `Aktiv · Zuletzt ${formatRelative(row.lastUsedAt)} · ${row.usageCount} Aufrufe gesamt`
              : 'Aktiv · Noch keine Nutzung'
            : 'Nicht freigeschaltet';

          return (
            <div key={row.toolKey} className="flex items-center justify-between gap-4 px-5 py-4">
              <div className="min-w-0">
                <p className="text-sm font-medium">{row.name}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{subtext}</p>
              </div>
              <Switch
                checked={row.granted && isActive}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  if (checked) {
                    void handleGrant(row);
                  } else {
                    setPendingRevoke(row);
                  }
                }}
              />
            </div>
          );
        })}
      </div>

      <AlertDialog open={pendingRevoke !== null} onOpenChange={(o) => !o && setPendingRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Zugang entziehen?</AlertDialogTitle>
            <AlertDialogDescription>
              Klient verliert sofort den Zugang zu {pendingRevoke?.name}. Seine Daten bleiben
              erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRevoke}>Zugang entziehen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
